import { useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from './ThemeContext';
import { THEME_SAVE_KEY } from './themeSave';
import { isThemePreference } from './themeStorage';

/**
 * Server to browser: the signed-in user's saved theme (`User.themePreference`) wins over this browser's
 * copy, so the choice follows the user across devices. Renders nothing. It sits inside AuthGate in the
 * agency tree (router.tsx AgencyRoot), so it mounts once a principal is loaded.
 *
 * Once per USER, not per value: the Appearance card's saves fold back into the principal one by one
 * (updateUser), and re-applying each answer would replay an older choice over a newer one when someone
 * clicks or arrows through the tiles quickly. An auth `refresh()` (avatar upload on My Profile, saving
 * Personal Voice on App Settings, creating a workspace, a change to your own membership) remounts this
 * under AuthGate, which adopts the then-current saved value again; a workspace switch does not.
 *
 * Except while a theme save is on its way (THEME_SAVE_KEY): /auth/me may have been read before that save
 * reached the server, so the choice on screen is the newer one and stays. The save's own answer then
 * folds into the principal as usual.
 *
 * A layout effect, so the shell's first frame is already in the user's theme rather than flipping one
 * frame after it paints.
 *
 * Not in the approval portal or the guides: the portal's principal is an external approver's guest
 * user, and adopting it would overwrite an agency user's copy in their own browser.
 */
export function ThemeServerSync() {
  const { principal } = useAuth();
  const { setPreference } = useTheme();
  const queryClient = useQueryClient();
  const adoptedFor = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!principal || adoptedFor.current === principal.user.id) return;
    adoptedFor.current = principal.user.id;
    if (queryClient.isMutating({ mutationKey: THEME_SAVE_KEY }) > 0) return;
    const saved = principal.user.themePreference;
    if (isThemePreference(saved)) setPreference(saved);
  }, [principal, setPreference, queryClient]);

  return null;
}
